import { forwardRef, useImperativeHandle, useRef, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo } from '@fortawesome/pro-light-svg-icons';
import { Button } from '@shared/components/ui/button';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { Input } from '@shared/components/ui/input';
import { Label } from '@shared/components/ui/label';
import { Alert, AlertDescription, AlertTitle } from '@shared/components/ui/shadcn/alert';
import { useToast } from '@shared/hooks/use-toast';
import { createHttpClient } from '@shared/adapters/HttpClient';
import { apiClientWH } from '@shared/services';
import { AmazonFbmApiAdapter } from '../../adapters/AmazonFbmApiAdapter';

export interface AmazonFbmSettingsModalRef {
  open: () => void;
}

interface AmazonFbmSettingsModalProps {
  onShopCreated?: () => void;
}

export const AmazonFbmSettingsModal = forwardRef<AmazonFbmSettingsModalRef, AmazonFbmSettingsModalProps>(
  ({ onShopCreated }, ref) => {
    const { t } = useTranslation('shopsModule');
    const { toast } = useToast();
    const modalRef = useRef<ModalCenterRef>(null);
    const [formData, setFormData] = useState({
      shopName: '',
    });
    const [isLoading, setIsLoading] = useState(false);

    const amazonFbmAdapter = new AmazonFbmApiAdapter(createHttpClient(apiClientWH));

    const isFormValid = useMemo(() => {
      return formData.shopName.trim() !== '';
    }, [formData]);

    useImperativeHandle(ref, () => ({
      open: () => {
        setFormData({
          shopName: '',
        });
        modalRef.current?.openModal();
      },
    }));

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const { id, value } = e.target;
      setFormData((prev) => ({ ...prev, [id]: value }));
    };

    const handleSave = async () => {
      if (!isFormValid) return;

      setIsLoading(true);

      try {
        const shopData = {
          name: formData.shopName,
        };

        const response = await amazonFbmAdapter.createAmazonFbmShop(shopData);

        if (response.success) {
          toast({
            title: t('modals.amazonFbm.toast.success.title'),
            description: response.message,
            variant: 'default',
          });

          modalRef.current?.closeModal();
          setFormData({
            shopName: '',
          });

          onShopCreated?.();
        } else {
          toast({
            title: t('modals.amazonFbm.toast.error.title'),
            description: response.message,
            variant: 'destructive',
          });
        }
      } catch (error: any) {
        toast({
          title: t('modals.amazonFbm.toast.error.title'),
          description: t('modals.amazonFbm.toast.error.unexpected'),
          variant: 'destructive',
        });
      } finally {
        setIsLoading(false);
      }
    };

    return (
      <ModalCenter ref={modalRef} title={t('modals.amazonFbm.title')}>
        <div className="space-y-6 max-h-[calc(100vh-100px)] overflow-y-auto">
          <Alert className="bg-body-bg mt-2">
            <div className="flex flex-col md:flex-row items-center gap-2 mb-2">
              <FontAwesomeIcon icon={faCircleInfo} className="w-5 h-5" />
              <AlertTitle className="text-lg">{t('modals.amazonFbm.alert.title')}</AlertTitle>
            </div>

            <AlertDescription className="text-icons text-sm">
              {t('modals.amazonFbm.alert.description')}
            </AlertDescription>
          </Alert>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="shopName">{t('modals.amazonFbm.labels.shopName')}</Label>
              <Input
                id="shopName"
                value={formData.shopName}
                onChange={handleChange}
                placeholder={t('modals.amazonFbm.placeholders.shopName')}
              />
            </div>
          </div>

          <div className="flex justify-end">
            <Button variant="primary" onClick={handleSave} disabled={!isFormValid || isLoading}>
              {isLoading ? t('modals.amazonFbm.buttons.saving') : t('modals.amazonFbm.buttons.save')}
            </Button>
          </div>
        </div>
      </ModalCenter>
    );
  },
);

AmazonFbmSettingsModal.displayName = 'AmazonFbmSettingsModal';
